import type React from 'react';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import omit from 'lodash/fp/omit';
import mapValues from 'lodash/mapValues';
import isString from 'lodash/isString';
import { useIntl } from 'react-intl';
import { ClassNames, css, useTheme } from '@emotion/react';
import useStores from 'hooks/use-stores.hooks';
import { observer } from 'mobx-react';
import { notification as antdNotification } from 'antd';
import { isTranslationId } from 'components/localization';

// Switch-off keeps passing this exact object, so its DOM is unchanged by construction.
const LEGACY_NOTICE_STYLE: CSSProperties = { whiteSpace: 'pre-line', width: 'fit-content', maxWidth: 500 };

/**
 * `className` lands on `.ant-notification-notice`, where antd declares `width` and `max-width` - so
 * those two need doubling. `white-space` it never declares.
 */
const newDesignNotice = css`
  white-space: pre-line;

  && {
    width: fit-content;
    max-width: 500px;
  }
`;

type EmitterProps = { noticeClassName?: string };

// error toasts never close on their own, and each api failure now carries its own request context, so
// dedup no longer collapses a burst of them - antd drops the oldest past this many
const MAX_VISIBLE_NOTIFICATIONS = 3;

const Emitter: React.FunctionComponent<EmitterProps> = ({ noticeClassName }) => {
  const { notificationStore } = useStores();
  const { notification } = notificationStore;
  const [notificationsApi, contextHolder] = antdNotification.useNotification({
    maxCount: MAX_VISIBLE_NOTIFICATIONS
  });

  // the resolved text, not the keys - two failures share `api.error.request-context` but differ once formatted
  const lastNotification = useRef<{ message: string; description?: string } | null>(null);

  const intl = useIntl();

  const onNotificationClose = useCallback(() => (lastNotification.current = null), []);

  useEffect(() => {
    if (notification) {
      if (!notification.message) {
        return;
      }

      // a variable can itself be a translation key (e.g. the resource name of a failed API call)
      const messageVariables =
        notification.messageVariables &&
        mapValues(notification.messageVariables, value =>
          isString(value) && isTranslationId(value) ? intl.formatMessage({ id: value }) : value
        );

      // api failures arrive here as the server's own English text, not as a translation key
      const message = isTranslationId(notification.message)
        ? intl.formatMessage({ id: notification.message }, messageVariables)
        : notification.message;

      const description =
        notification.description && isTranslationId(notification.description)
          ? intl.formatMessage({ id: notification.description }, notification.descriptionVariables)
          : notification.description;

      if (lastNotification.current?.message !== message || lastNotification.current?.description !== description) {
        // only way to get an html identifier on the notification
        const e2eClassName = `e2e-${notification.notificationType}-notification`;

        notificationsApi[notification.notificationType]({
          ...omit(['notificationType', 'messageVariables', 'descriptionVariables'], notification),
          message,
          description,
          style: noticeClassName ? undefined : LEGACY_NOTICE_STYLE,
          className: noticeClassName ? `${e2eClassName} ${noticeClassName}` : e2eClassName,
          onClose: onNotificationClose,
          duration: notification.notificationType === 'error' ? 0 : 5
        });

        lastNotification.current = { message, description };
      }
    }
  }, [notificationsApi, intl, notification, onNotificationClose, noticeClassName]);

  return contextHolder;
};

const NotificationEmitter = observer(Emitter);

/**
 * The class has to be a string before the effect can hand it to antd's imperative API, and
 * `ClassNames` is a render prop - hence the split from the emitter above. `toClassName` is called only
 * on the new-design path, so switch-off injects no stylesheet at all.
 */
const NotificationManager: React.FunctionComponent = () => {
  const { isNewDesign } = useTheme();

  return (
    <ClassNames>
      {({ css: toClassName }) => (
        <NotificationEmitter noticeClassName={isNewDesign ? toClassName(newDesignNotice) : undefined} />
      )}
    </ClassNames>
  );
};

export default observer(NotificationManager);
